import { DrawingType, MAP_TOOLS } from "../../../context/RulerContext";
import { Button } from "../../ui/buttons/button";
import { Pentagon, Ruler, SquareDashed } from "lucide-react";
import {
  REPORT_PROJECTION_TYPES,
  type ReportProjectionType,
} from "../../../global/constants/projectionLayers";
import type { EnergyType, ReportScope } from "../model/types";
import { MARKINGS_PER_SCOPE } from "../hooks/useReportState";

type ReportMarking = {
  id: string;
  name: string;
  type: DrawingType;
};

const SCOPES: Array<{ value: ReportScope; label: string }> = [
  { value: "marcacao", label: "Uma área" },
  { value: "comparativo", label: "Comparar duas áreas" },
  { value: "estado", label: "Pernambuco inteiro" },
];

const ENERGIES: Array<{ value: EnergyType; label: string }> = [
  { value: "solar", label: "Solar" },
  { value: "eolico", label: "Eólica" },
];

interface ReportOptionsProps {
  scope: ReportScope;
  energies: EnergyType[];
  level: ReportProjectionType;
  includeContextLayers: boolean;
  markings: ReportMarking[];
  selectedMarkings: string[];
  onSelectScope: (scope: ReportScope) => void;
  onToggleEnergy: (energy: EnergyType) => void;
  onSelectLevel: (level: ReportProjectionType) => void;
  onToggleContextLayers: () => void;
  onToggleMarking: (id: string) => void;
  disabled?: boolean;
}

function OptionGroup({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div>
        <p className="text-[13px] font-medium">{label}</p>
        {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
      </div>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

export function ReportOptions({
  scope,
  energies,
  level,
  includeContextLayers,
  markings,
  selectedMarkings,
  onSelectScope,
  onToggleEnergy,
  onSelectLevel,
  onToggleContextLayers,
  onToggleMarking,
  disabled = false,
}: ReportOptionsProps) {
  const getMarkingIcon = (type: DrawingType) => {
    if (type === MAP_TOOLS.MEASURE_AREA) return <Pentagon />;
    if (type === MAP_TOOLS.CITY) return <SquareDashed />;
    return <Ruler />;
  };

  const needed = MARKINGS_PER_SCOPE[scope];
  const markingHint =
    scope === "comparativo"
      ? `Escolha duas Marcações (${selectedMarkings.length}/2). A primeira é a área A.`
      : "Escolha a Marcação que o relatório vai analisar.";

  return (
    <div className="flex flex-col gap-4">
      <OptionGroup label="O que analisar?">
        {SCOPES.map((s) => (
          <Button
            key={s.value}
            variant={scope === s.value ? "default" : "outline"}
            size="sm"
            onClick={() => onSelectScope(s.value)}
            disabled={disabled}
            aria-pressed={scope === s.value}
          >
            {s.label}
          </Button>
        ))}
      </OptionGroup>

      {needed > 0 && (
        <OptionGroup label="Marcações" hint={markingHint}>
          {markings.length === 0 ? (
            <p className="text-[12px] text-muted-foreground">
              Nenhuma marcação realizada. Desenhe uma área no mapa ou selecione um
              município.
            </p>
          ) : (
            markings.map((marking) => {
              const position = selectedMarkings.indexOf(marking.id);
              const selected = position >= 0;
              return (
                <Button
                  key={marking.id}
                  asChild
                  variant={selected ? "default" : "outline"}
                  size="sm"
                  className="max-w-full cursor-default"
                  onClick={() => onToggleMarking(marking.id)}
                >
                  <span
                    title={marking.name}
                    aria-pressed={selected}
                    className={disabled ? "pointer-events-none opacity-50" : ""}
                  >
                    {getMarkingIcon(marking.type)}
                    <span className="max-w-[180px] truncate">{marking.name}</span>
                    {scope === "comparativo" && selected && (
                      <span className="text-[10px] font-bold">
                        {position === 0 ? "A" : "B"}
                      </span>
                    )}
                  </span>
                </Button>
              );
            })
          )}
        </OptionGroup>
      )}

      <OptionGroup label="Tipo de energia">
        {ENERGIES.map((e) => (
          <Button
            key={e.value}
            variant={energies.includes(e.value) ? "default" : "outline"}
            size="sm"
            onClick={() => onToggleEnergy(e.value)}
            disabled={disabled}
            aria-pressed={energies.includes(e.value)}
          >
            {e.label}
          </Button>
        ))}
      </OptionGroup>

      <OptionGroup
        label="Nível de detalhamento"
        hint="Nível de projeção detalhado nos memoriais. A tabela da cascata mostra todos."
      >
        {REPORT_PROJECTION_TYPES.map((option) => (
          <Button
            key={option.value}
            variant={level === option.value ? "default" : "outline"}
            size="sm"
            onClick={() => onSelectLevel(option.value)}
            disabled={disabled}
            aria-pressed={level === option.value}
          >
            {option.label}
          </Button>
        ))}
      </OptionGroup>

      <label className="flex items-start gap-2 text-[13px] cursor-pointer">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={includeContextLayers}
          onChange={onToggleContextLayers}
          disabled={disabled}
        />
        <span>
          <span className="font-medium">Incluir camadas de contexto</span>
          <span className="block text-[11px] text-muted-foreground">
            Mapa e legendas das camadas ligadas no painel de Camadas.
          </span>
        </span>
      </label>
    </div>
  );
}
